import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider } from "@/context/SiteContext";
import { captureUtm } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import Home from "@/pages/Home";
import FinalExpense from "@/pages/FinalExpense";
import TermLife from "@/pages/TermLife";
import About from "@/pages/About";
import Carriers from "@/pages/Carriers";
import FAQ from "@/pages/FAQ";
import Quote from "@/pages/Quote";
import Contact from "@/pages/Contact";
import Schedule from "@/pages/Schedule";
import ThankYou from "@/pages/ThankYou";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Accessibility from "@/pages/Accessibility";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";

function useLenis() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.12, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    captureUtm();
    if (hash) {
      const el = document.querySelector(hash);
      if (el) { el.scrollIntoView(); return; }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function Layout({ children }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[60] focus:bg-gold focus:text-navy focus:px-4 focus:py-3 focus:font-semibold">
        Skip to main content
      </a>
      {!isAdmin && <Header />}
      <main id="main-content" className="flex-1 pb-20 md:pb-0">{children}</main>
      {!isAdmin && <Footer />}
      {!isAdmin && <StickyMobileCTA />}
    </div>
  );
}

export default function App() {
  useLenis();
  return (
    <SiteProvider>
      <BrowserRouter>
        <ScrollManager />
        <Toaster richColors position="top-center" />
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/final-expense" element={<FinalExpense />} />
            <Route path="/term-life" element={<TermLife />} />
            <Route path="/about" element={<About />} />
            <Route path="/carriers" element={<Carriers />} />
            <Route path="/faqs" element={<FAQ />} />
            <Route path="/quote" element={<Quote />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/accessibility" element={<Accessibility />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Home />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </SiteProvider>
  );
}
